import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Wand2, Sparkles, Send, Download, Check, Link as LinkIcon, RotateCcw } from "lucide-react";
import axios from "axios";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { API, recordCardOpen, getThread } from "../lib/api";
import { MoodCard } from "../components/MoodCard";
import ExportCard from "../components/ExportCard";
import MoodCardSleeve from "../components/MoodCardSleeve";
import ReactionPanel from "../components/ReactionPanel";
import CrisisHelpFooter from "../components/CrisisHelpFooter";
import SleeveIntro from "../components/SleeveIntro";
import { shareCardNative } from "../lib/webShare";
import { Toaster } from "../components/ui/sonner";
import { buildShareCaption, buildShareCaptionText } from "../lib/shareCaption";
import { buildPrivateShareMessage, pickPreviewTitle, pickPreviewDescription } from "../lib/privateShareMessage";
import { getViewerId } from "../lib/viewerId";
import { track } from "../lib/track";
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";

// Resolved at build-time from env so dev/preview/prod each emit the right
// iter-81: share URLs MUST always be production-canonical (moodcard.app).
// We import the helper from shareUrl.js — single source of truth.
// Previously this file used REACT_APP_BACKEND_URL as the share host, which
// leaked the preview URL into every navigator.share / clipboard copy
// (recipients landed on …preview.emergentagent.com instead of moodcard.app).
import { canonicalShareUrl } from "../lib/shareUrl";
import { getPackAccent } from "../lib/stylePacks";
import { getHistory } from "../lib/history";
import ParticleRain from "../components/ParticleRain";
const PUBLIC_DOMAIN = "https://moodcard.app";

export default function PublicCard() {
    const { slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [mood, setMood] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [opened, setOpened] = useState(false);
    // iter-99 — memory replay state. When the sender opens their own card
    // and it has accrued reactions, fire the heart/flame rain once per
    // session so they FEEL the impact.
    const [memoryReplay, setMemoryReplay] = useState({ felt: 0, candle: 0, trigger: 0 });
    const [savedFlash, setSavedFlash] = useState(false);
    const [copiedFlash, setCopiedFlash] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    const exportRef = useRef(null);

    // ?to=Mira — optional recipient passed by the sender from ShareSheet.
    // Sanitized at read time: trimmed, alphabetic + spaces only, capped at
    // 24 chars, Capital-Cased. Anything else is dropped so the moment
    // never reads as junk.
    const recipient = useMemo(() => {
        const raw = new URLSearchParams(location.search).get("to") || "";
        const cleaned = raw.replace(/[^a-zA-Z \-']/g, "").trim().slice(0, 24);
        if (!cleaned) return null;
        return cleaned
            .split(/\s+/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ");
    }, [location.search]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/c/${slug}`);
                if (!cancelled) {
                    setMood(data);
                    track("public_card_viewed", { slug });
                }
            } catch (err) {
                if (!cancelled) {
                    setNotFound(true);
                    // Track 404s so we can spot leak patterns (preview links
                    // shared into prod, deleted-card aftermath, typos).
                    // Includes the referrer to distinguish organic vs shared.
                    track("public_card_not_found", {
                        slug,
                        status: err?.response?.status || 0,
                        referrer: document.referrer || "direct",
                    });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    // iter-99 — sender memory replay. When the SENDER opens their own
    // card and it has accrued reactions, fire the heart/flame rain ONCE
    // per session so they get a visceral hit of "people felt this".
    //
    // Sender detection: the card's slug exists in this browser's local
    // history (history.js writes a slug for every card the user creates).
    // Session-keyed via sessionStorage so the replay fires once per card
    // per session (not on every interaction).
    useEffect(() => {
        if (!opened || !mood) return;
        try {
            const inHistory = getHistory().some((h) => h.slug === mood.slug);
            if (!inHistory) return;
            const sessionKey = `moodcard.replay.${mood.slug}`;
            if (window.sessionStorage.getItem(sessionKey)) return;
            const r = mood.reactions || {};
            const felt = Math.min(Number(r.felt) || 0, 20);
            const candle = Math.min(Number(r.candle) || 0, 12);
            if (felt <= 0 && candle <= 0) return;
            // Mark replayed BEFORE the timer so any double-mount/race
            // doesn't re-fire.
            window.sessionStorage.setItem(sessionKey, "1");
            // Slight delay so the card open animation completes first.
            const t = setTimeout(() => {
                setMemoryReplay({ felt, candle, trigger: Date.now() });
            }, 900);
            return () => clearTimeout(t);
        } catch { /* localStorage may be blocked — skip silently */ }
    }, [opened, mood]);

    // Soft micro-reply ping — fires once per (slug, viewer) when ?to= is
    // present. The sender's dashboard surfaces this as "Mira opened your
    // card · 2 min ago". Skips entirely when the URL is a generic share
    // (no recipient = no need to alert the sender). Silent on failure.
    useEffect(() => {
        if (!mood || !recipient) return;
        const viewerId = getViewerId();
        if (!viewerId) return;
        recordCardOpen(slug, recipient, viewerId).catch(() => { /* silent */ });
    }, [mood, recipient, slug]);

    // Thread length probe — only surface "view correspondence" affordance
    // when this card sits in a chain of >=3. Lazy fetch so it doesn't
    // delay the primary card render. Network failure is silent.
    const [threadLen, setThreadLen] = useState(0);
    useEffect(() => {
        if (!mood?.id) return;
        let cancelled = false;
        getThread(mood.id)
            .then((res) => { if (!cancelled) setThreadLen(res.count || 0); })
            .catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [mood?.id]);

    if (notFound) {
        return (
            <div className="min-h-screen bg-app text-app flex flex-col items-center justify-center px-6 text-center">
                {/* Eyebrow + main line — keeps the editorial voice even
                    when the destination is gone. */}
                <p
                    className="font-sans uppercase tracking-[0.42em] text-app-3 text-[10px] mb-5"
                    data-testid="public-card-not-found-eyebrow"
                >
                    ✦ this card is no longer here
                </p>
                <p
                    className="font-serif italic text-app text-xl sm:text-2xl mb-3 max-w-md"
                    data-testid="public-card-not-found-title"
                >
                    this mood drifted away.
                </p>
                <p
                    className="font-serif text-app-2 text-sm sm:text-base mb-8 max-w-sm leading-relaxed"
                    data-testid="public-card-not-found-explainer"
                >
                    the sender may have removed it, or this link belongs to a
                    different place. either way — your turn.
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-3">
                    <button
                        onClick={() => navigate("/")}
                        data-testid="public-card-not-found-primary-cta"
                        className="btn-press px-7 py-3 rounded-full bg-app text-app-inverse font-sans text-sm tracking-wide hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: "var(--app-text)" }}
                    >
                        make your own
                    </button>
                    <button
                        onClick={() => navigate("/explore")}
                        data-testid="public-card-not-found-secondary-cta"
                        className="btn-press px-6 py-3 rounded-full border border-app/30 text-app font-sans text-sm tracking-wide hover:bg-surface-1 transition-colors"
                    >
                        see what others wrote
                    </button>
                </div>

                {/* Tiny diagnostic chip — surfaces the slug for support
                    requests without making it the focus. */}
                {slug && (
                    <p
                        className="mt-10 font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 opacity-55"
                        data-testid="public-card-not-found-slug"
                    >
                        ref · {slug}
                    </p>
                )}
            </div>
        );
    }

    if (!mood) {
        return (
            <div className="min-h-screen w-full bg-app text-app flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="public-card-loading">
                <div className="absolute inset-0 aurora-wash opacity-30 pointer-events-none" />
                <div className="absolute inset-0 grain pointer-events-none" />
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="relative z-10 flex flex-col items-center"
                >
                    <motion.span
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        className="font-sans text-[11px] uppercase tracking-[0.5em] text-app font-medium"
                    >
                        MOODCARD
                    </motion.span>
                    <span className="mt-3 font-serif italic text-[13px] text-app-3">
                        a feeling someone wanted to share.
                    </span>
                </motion.div>
            </div>
        );
    }

    const card = mood.cards.find((c) => c.style === (mood.selected_style || mood.cards[0].style)) || mood.cards[0];
    const shareUrl = canonicalShareUrl(`${PUBLIC_DOMAIN}/open/${slug}`);
    const remixUrl = canonicalShareUrl(`${PUBLIC_DOMAIN}/remix/${slug}`);
    // Phase 4·c — mysterious OG. Title NEVER spoils the card; instead rotates
    // through the spec's PREVIEW_TITLES seeded by slug so the same card
    // always previews the same way (good for cache + WhatsApp re-fetch).
    const ogTitle = pickPreviewTitle(slug);
    const ogDesc = pickPreviewDescription(slug);
    // Server-rendered branded OG image — peek + seal + "open gently".
    // Cache-bust by card id so re-pack / re-generate refreshes social previews.
    const ogImage = `${process.env.REACT_APP_BACKEND_URL}/api/og/open/${slug}.png?v=${(mood.id || "x").slice(-6)}`;

    const handleMakeMine = () => {
        track("public_card_make_own_clicked", { slug });
        navigate("/");
    };

    // Reply loop — when this card has an addressable sender (first name
    // exposed by the backend), tapping "send back" routes the recipient
    // home with state so Home + Loading + CardDetail can pre-address the
    // reply card back to the original sender. No chat layer, just one
    // card each way.
    const senderName = mood?.sender_name || null;
    const handleReplyBack = () => {
        if (!senderName) return handleMakeMine();
        track("public_card_reply_clicked", { slug, sender: senderName });
        // sessionStorage persists across the create flow without polluting
        // localStorage (which would survive across tabs / unrelated sessions).
        try {
            window.sessionStorage.setItem("moodcard.reply_to", senderName);
            window.sessionStorage.setItem("moodcard.reply_from_slug", slug || "");
        } catch { /* ignore */ }
        navigate("/", { state: { reply_to: senderName, reply_from_slug: slug } });
    };

    const handleRemix = () => {
        track("remix_clicked", { slug });
        navigate(`/remix/${slug}`);
    };

    const handleSavePng = async () => {
        if (!exportRef.current) return;
        setExporting(true);
        try {
            const dataUrl = await toPng(exportRef.current, {
                cacheBust: true,
                pixelRatio: 2,
                backgroundColor: "#050507",
                skipFonts: true,
            });
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `moodcard-${card.style}-${slug}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setSavedFlash(true);
            track("public_card_saved_png", { slug, style: card.style });
            setTimeout(() => setSavedFlash(false), 1800);
        } catch (e) {
            console.error(e);
            toast.error("Couldn't save image");
        } finally {
            setExporting(false);
        }
    };

    const handleSendTo = async () => {
        // Prefer the soft "someone left you a feeling" framing for public-page shares.
        const priv = buildPrivateShareMessage({ card, mood, shareUrl });
        const viralCaption = buildShareCaption({ card, shareUrl });
        track("public_card_send_clicked", { slug, tone: priv.tone });
        // iter-85 — use Web Share Level 2 with the rendered OG image as a
        // File attachment. This lets recipients see the visual immediately
        // in the share sheet → instant share to IG Story / WhatsApp / X /
        // without any download step. Falls back to URL-only share, then
        // clipboard, if the OS doesn't support files.
        const ok = await shareCardNative({
            title: "Someone left you a MoodCard.",
            text: priv.text,
            url: shareUrl,
            fileUrl: ogImage,
            fileName: `moodcard-${slug || "card"}.png`,
        });
        if (ok) return;
        // Fallback: copy the private message + URL.
        try {
            await navigator.clipboard.writeText(priv.text || viralCaption);
            setCopiedFlash(true);
            toast.success("Link copied — paste it anywhere");
            setTimeout(() => setCopiedFlash(false), 1800);
        } catch {
            toast.error("Couldn't copy");
        }
    };

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="public-card-screen">
            <Helmet>
                <title>{`${ogTitle} — MoodCard`}</title>
                <link rel="canonical" href={shareUrl} />
                <meta name="description" content={ogDesc} />
                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="MoodCard" />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDesc} />
                <meta property="og:url" content={shareUrl} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={ogTitle} />
                {/* Twitter / X */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@moodcardapp" />
                <meta name="twitter:title" content={ogTitle} />
                <meta name="twitter:description" content={ogDesc} />
                <meta name="twitter:image" content={ogImage} />
                <meta name="twitter:image:alt" content={ogTitle} />
            </Helmet>

            <Toaster theme="dark" position="bottom-center" />

            {/* Background — gentle aurora + grain. Dims more deeply when card opens
                so the reveal is the only thing in focus. */}
            <motion.div
                className="absolute inset-0 aurora-wash pointer-events-none"
                animate={{ opacity: opened ? 0.10 : 0.32, filter: opened ? "blur(12px)" : "blur(0px)" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            />
            <div className="absolute inset-0 grain pointer-events-none" />

            {/* iter-73: clean mobile top bar (matches CardDetail). Desktop keeps
                the legacy compact header. */}
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                onOpenNotifications={() => navigate("/")}
                showLeft
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />

            <header className="hidden lg:flex relative z-10 max-w-md mx-auto px-6 pt-6 items-center justify-between">
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                    moodcard · public
                </span>
                <button
                    onClick={handleMakeMine}
                    className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-2 hover:text-app transition-colors"
                    data-testid="public-card-brand-link"
                >
                    moodcard.app
                </button>
            </header>

            {/* iter-82: tightened top padding (was pt-14) and bumped card-zone
                height so the card occupies ~70-75% of mobile screen real estate
                per the UX audit (was hovering at ~50%). Bottom action tray stays
                comfortable via pb-24. */}
            {/* iter-92 — mobile is a viewport-locked flex column so the card
                occupies the full available width AND vertical space (no dead
                space). Desktop keeps the legacy max-w-md block layout. */}

            {/* Hidden offscreen export node — pixel-locked ExportCard for save-png.
                Rendered once at root so both mobile and desktop layouts share it. */}
            <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }}>
                <div ref={exportRef}>
                    <ExportCard card={card} width={1080} height={1920} showWatermark={mood.watermark_required !== false} />
                </div>
            </div>

            {/* Phase 4·e — "a feeling has arrived" intro overlay (z-60 fixed,
                spans both mobile + desktop, only shown on first visit per slug). */}
            <SleeveIntro slug={slug} recipient={recipient} onDone={() => { /* sleeve already mounted underneath */ }} />

            {/* MOBILE LAYOUT — full-bleed, viewport-locked.
                iter-93 — card now uses 98% width × 90% height with always-on
                halo glow and the action dock OVERLAID on the bottom of the
                card (not below it). No dead space; the card is the page. */}
            <div
                className="sm:hidden relative z-10 flex flex-col items-center justify-center"
                style={{
                    height: "calc(100dvh - 56px)",
                    padding: "4px 1vw 0 1vw",
                    "--pack-accent": getPackAccent(mood?.pack || "cinematic"),
                }}
                data-testid="public-card-mobile-zone"
            >
                {/* Card zone — fills 98% width × 90% available height. The
                    sleeve is height-bounded via `fill={true}` so it scales
                    to the available area while preserving 9:16 aspect. */}
                <div
                    className="relative w-full flex items-center justify-center"
                    style={{ height: "90%", width: "98%" }}
                >
                    <MoodCardSleeve
                        card={card}
                        moodId={mood?.id || mood?.mood_id || null}
                        showWatermark={mood.watermark_required !== false}
                        onOpened={() => setOpened(true)}
                        lane={mood.lane || null}
                        temperature={mood.temperature || null}
                        fill={true}
                    />

                    {/* Always-on halo glow — soft radial premium light behind
                        the card. Per-pack accent tint. Pointer-events none. */}
                    <div
                        className="pointer-events-none absolute inset-0 -z-10"
                        style={{
                            background: `radial-gradient(60% 50% at 50% 50%, var(--pack-accent, rgba(232,213,168,0.18)) 0%, transparent 70%)`,
                            filter: "blur(40px)",
                        }}
                        aria-hidden="true"
                    />
                </div>

                {/* iter-101 — anticipation strip REMOVED. The wax-seal
                    instruction ("tap the seal to open") inside the sleeve
                    is the only directive needed. Stripped duplicate copy. */}

                {/* Ambient drift particles — only when card is opened */}
                {opened && (
                    <div className="mc-ambient-drift" aria-hidden="true">
                        <span className="mc-drift-dot mc-drift-1" />
                        <span className="mc-drift-dot mc-drift-2" />
                        <span className="mc-drift-dot mc-drift-3" />
                    </div>
                )}

                {/* iter-99 — sender memory replay overlay. Renders only
                    when memoryReplay.trigger > 0. Felt + candle fire
                    simultaneously so the sender sees the full reaction
                    history land. Followed by a brief "N felt this" caption
                    so the moment has emotional language attached. */}
                {memoryReplay.trigger > 0 && (
                    <>
                        <ParticleRain
                            trigger={memoryReplay.trigger}
                            kind="felt"
                            count={memoryReplay.felt}
                            accent="rgb(244, 114, 182)"
                        />
                        <ParticleRain
                            trigger={memoryReplay.trigger + 1}
                            kind="candle"
                            count={memoryReplay.candle}
                            accent="rgb(251, 191, 36)"
                        />
                        <motion.div
                            key={`replay-caption-${memoryReplay.trigger}`}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: [0, 1, 1, 0], y: [6, 0, 0, -4] }}
                            transition={{ duration: 3.6, times: [0, 0.18, 0.78, 1], ease: "easeOut" }}
                            className="pointer-events-none fixed top-[72px] left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full bg-black/60 backdrop-blur-xl border-faint border"
                            data-testid="sender-memory-caption"
                        >
                            <p className="eyebrow-wide text-app">
                                {memoryReplay.felt > 0 && (
                                    <span className="text-rose-200">
                                        {memoryReplay.felt} felt this
                                    </span>
                                )}
                                {memoryReplay.felt > 0 && memoryReplay.candle > 0 && (
                                    <span className="text-app-3"> · </span>
                                )}
                                {memoryReplay.candle > 0 && (
                                    <span className="text-amber-200">
                                        {memoryReplay.candle} held it
                                    </span>
                                )}
                            </p>
                        </motion.div>
                    </>
                )}

                {/* Floating action dock — appears after open. Sits as
                    fixed-positioned overlay (ReactionPanel internally uses
                    `fixed bottom-3` when dock=true). */}
                <AnimatePresence>
                    {opened && (
                        <ReactionPanel
                            key="dock"
                            slug={slug}
                            initialCounts={mood?.reactions || {}}
                            onShare={handleSendTo}
                            onRemix={() => { track("remix_clicked", { slug }); navigate(`/remix/${slug}`); }}
                            dock={true}
                        />
                    )}
                </AnimatePresence>

                {/* iter-80: helpline footer for crisis cues. */}
                {opened && (
                    <CrisisHelpFooter
                        show={!!mood?.crisis_flag}
                        locality={mood?.locality}
                    />
                )}

                {/* iter-93 — remix counter shown when ≥1 remix exists. Tiny
                    pill above the dock, reinforces social proof. */}
                {opened && (mood?.remix_count || 0) > 0 && (
                    <button
                        onClick={() => navigate(`/c/${slug}/remixes`)}
                        data-testid="public-remix-count-pill"
                        className="absolute z-30 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/55 backdrop-blur-md border border-white/15 font-sans text-[10px] uppercase tracking-[0.28em] text-app/90 hover:bg-black/70 transition-colors"
                        style={{ bottom: "calc(96px + env(safe-area-inset-bottom))" }}
                    >
                        <span className="text-app font-medium">{mood.remix_count}</span>
                        <span className="ml-1.5 text-app-3">
                            {mood.remix_count === 1 ? "remix" : "remixes"} · tap to see
                        </span>
                    </button>
                )}
            </div>

            {/* DESKTOP LAYOUT — preserved from the previous design. */}
            <div className="hidden sm:block relative z-10 max-w-md mx-auto px-5 pt-6 pb-24">
                {/* Anticipation tagline */}
                <AnimatePresence>
                    {!opened && (
                        <motion.div
                            key="anticipation-desktop"
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.9, delay: 0.3 }}
                            className="mb-5 text-center"
                        >
                            <p className="font-serif italic text-[15px] text-app leading-relaxed">
                                someone trusted you with this.
                            </p>
                            <p className="mt-2 font-serif italic text-[12px] text-app-3">
                                for your eyes only.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Sleeve interaction */}
                <MoodCardSleeve
                    card={card}
                    moodId={mood?.id || mood?.mood_id || null}
                    showWatermark={mood.watermark_required !== false}
                    onOpened={() => setOpened(true)}
                    lane={mood.lane || null}
                    temperature={mood.temperature || null}
                />

                {/* Ambient drift particles — only when card is opened */}
                {opened && (
                    <div className="mc-ambient-drift" aria-hidden="true">
                        <span className="mc-drift-dot mc-drift-1" />
                        <span className="mc-drift-dot mc-drift-2" />
                        <span className="mc-drift-dot mc-drift-3" />
                    </div>
                )}

                {/* Action tray — appears AFTER card opens. */}
                <AnimatePresence>
                    {opened && (
                        <motion.div
                            key="tray-desktop"
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                            className="mt-10"
                            data-testid="card-action-tray"
                        >
                            <ReactionPanel
                                slug={slug}
                                initialCounts={mood?.reactions || {}}
                                onShare={handleSendTo}
                                onRemix={() => { track("remix_clicked", { slug }); navigate(`/remix/${slug}`); }}
                            />

                            <CrisisHelpFooter
                                show={!!mood?.crisis_flag}
                                locality={mood?.locality}
                            />

                            {senderName && (
                                <button
                                    onClick={handleReplyBack}
                                    data-testid="public-reply-back-button"
                                    className="mt-3 w-full text-center font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 hover:text-app py-1.5 transition-colors"
                                >
                                    or send a feeling back to {senderName}
                                </button>
                            )}

                            {threadLen >= 3 && mood?.id && (
                                <button
                                    onClick={() => navigate(`/thread/${mood.id}`)}
                                    data-testid="public-view-thread-button"
                                    className="mt-2 w-full flex items-center justify-center gap-2 font-sans text-[10px] uppercase tracking-[0.32em] text-amber-200/75 hover:text-amber-100 py-1.5 transition-colors"
                                >
                                    <span className="h-[3px] w-[3px] rounded-full bg-current opacity-80" />
                                    view the correspondence · {threadLen}
                                </button>
                            )}

                            <div className="grid mt-4 grid-cols-3 gap-2.5">
                                <button
                                    onClick={handleRemix}
                                    data-testid="tray-remix"
                                    className="mc-tray-btn"
                                >
                                    <Wand2 className="w-4 h-4" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] leading-tight">Remix the feeling</span>
                                </button>

                                <button
                                    onClick={handleSendTo}
                                    data-testid="tray-send"
                                    className="mc-tray-btn"
                                >
                                    {copiedFlash ? <Check className="w-4 h-4" strokeWidth={1.8} /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                                    <span className="font-sans text-[10px] leading-tight">{copiedFlash ? "Copied" : "Send quietly"}</span>
                                </button>

                                <button
                                    onClick={handleSavePng}
                                    disabled={exporting}
                                    data-testid="tray-save"
                                    className="mc-tray-btn disabled:opacity-50"
                                >
                                    {savedFlash ? <Check className="w-4 h-4" strokeWidth={1.8} /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
                                    <span className="font-sans text-[10px] leading-tight">{savedFlash ? "Saved" : "Keep this close"}</span>
                                </button>
                            </div>

                            <p className="mt-5 text-center font-serif italic text-[11px] text-app-3">
                                no signup. just you and the feeling.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pre-open subtle byline */}
                <AnimatePresence>
                    {!opened && (
                        <motion.p
                            key="prebyline"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.7, delay: 1.0 }}
                            className="mt-12 text-center font-serif italic text-[12px] text-app-3"
                        >
                            a feeling waits inside.
                        </motion.p>
                    )}
                </AnimatePresence>
            </div>

            <div className="pointer-events-none absolute bottom-6 left-0 right-0 text-center hidden sm:block">
                <span className="font-serif italic text-[11px] text-app-3">
                    made with <span className="not-italic font-sans tracking-[0.25em] uppercase">MoodCard</span>
                </span>
            </div>
        </div>
    );
}
